import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Play, X, Maximize, Minimize, Loader2 } from 'lucide-react';
import { propertyData } from '../../data/mock';
import { setGlobalActivePlayer } from './HeroSection';

const MarketVideoSection = ({ heroVideoPlaying }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);

  // YouTube video ID
  const youtubeId = propertyData.marketYouTubeId;

  // Pause when hero video starts playing
  useEffect(() => {
    if (heroVideoPlaying && isPlaying) {
      setShowVideo(false);
      setIsPlaying(false);
    }
  }, [heroVideoPlaying, isPlaying]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Play video - shows YouTube embed
  const playVideo = useCallback(() => {
    setIsLoading(true);
    setShowVideo(true);
    setIsPlaying(true);
    
    // Register as active player
    setGlobalActivePlayer({
      pause: () => {
        setShowVideo(false);
        setIsPlaying(false);
      }
    });
  }, []);

  // Close video
  const closeVideo = useCallback(() => {
    setShowVideo(false);
    setIsPlaying(false);
    setIsLoading(false);
  }, []);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  // Handle iframe load
  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  return (
    <section id="market-evidence" className="py-24 bg-gradient-to-b from-[#0d1117] to-[#0a0c10]" data-testid="market-video-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-block px-4 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium tracking-wide mb-4">
            Market Position
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white mb-4">
            CENTRAL FLORIDA OPPORTUNITY
          </h2>
          <p className="text-lg text-white/70 font-light">
            Discover the unique advantages of this exceptional property location
          </p>
        </div>

        {/* Video Player Container */}
        <div 
          ref={containerRef}
          className="relative max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-2xl shadow-black/50 bg-black"
        >
          {/* 16:9 Aspect Ratio Container */}
          <div className="relative aspect-video">
            {/* Poster Image - Visible when not playing */}
            {!showVideo && (
              <div className="absolute inset-0">
                <img
                  src={propertyData.images.aerial}
                  alt="William Lane Estate Aerial View"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30 hover:bg-black/40 transition-all cursor-pointer" onClick={playVideo}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button
                      className="w-24 h-24 rounded-full bg-amber-500/90 backdrop-blur-sm flex items-center justify-center text-white shadow-2xl shadow-amber-500/40 hover:bg-amber-500 hover:scale-110 transition-all duration-300"
                      data-testid="market-video-play-btn"
                    >
                      <Play className="w-10 h-10 ml-1" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* YouTube Embed - Visible when playing */}
            {showVideo && (
              <>
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&origin=${window.location.origin}`}
                  title="Market Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  onLoad={handleIframeLoad}
                  data-testid="market-youtube-iframe"
                />
                
                {/* Loading overlay */}
                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10 pointer-events-none">
                    <Loader2 className="w-12 h-12 text-amber-400 animate-spin" />
                  </div>
                )}

                {/* Controls */}
                <div className="absolute top-4 right-4 flex items-center space-x-2 z-20">
                  <button
                    onClick={toggleFullscreen}
                    className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center text-white hover:bg-black/80 transition-all"
                    data-testid="market-video-fullscreen-btn"
                  >
                    {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={closeVideo}
                    className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center text-white hover:bg-black/80 transition-all"
                    data-testid="market-video-close-btn"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        
        <p className="text-center text-white/50 text-sm mt-6">
          Click play to discover why Central Florida represents exceptional value for discerning buyers
        </p>
      </div>
    </section>
  );
};

export default MarketVideoSection;
